// src/AuthPage.tsx
//
// Přihlášení a registrace (/ i /login). Obrazovka žije v AppShellu bez spodní
// navigace (App.tsx), titulek hlavičky přepíná podle režimu přes useAppHeader.
// Přihlášeného a ověřeného uživatele přesměruje stav z useAuth (AuthProvider) –
// žádný vlastní onAuthStateChanged. Validační chyby se ukazují u polí, chyby
// Firebase v toastu; výsledek registrace a neověřený e-mail zůstávají jako
// trvalá zpráva v obsahu s tlačítkem pro nové odeslání ověřovacího e-mailu.
//
// Firebase Auth flow: výchozí úložiště přihlášení (IndexedDB, sdílí ho
// všechny záložky – žádné setPersistence, viz handleLogin), continue URL
// /email-verified (zde) a /password-reset-complete (auth.ts), neověřený účet
// se po přihlášení i po registraci hned zase odhlásí.
//
// Registrace ve dvou krocích (bezpečnost a, utils/completeRegistration):
// registrace založí jen účet a údaje formuláře uloží do pendingProfiles/{uid};
// profil users/{uid} (pořadové číslo, převod importovaného profilu se stejným
// e-mailem) vznikne až při prvním přihlášení s OVĚŘENÝM e-mailem – Firebase
// Auth založí účet i s cizím e-mailem, takže dřívější převod hned při
// registraci vydal cizí importovaný profil komukoli, kdo znal e-mail.
//
// Když dokončení nejde provést samo, přihlášený ověřený uživatel místo
// formulářů uvidí krok, který ho dokončí (2. kolo revize, nálezy 8 a 10):
//  - „Jste to vy?“ – importy se stejným e-mailem, ale jiným jménem;
//  - „Doplňte údaje“ – účet bez profilu i rozpracovaného profilu (registrace,
//    které selhalo uložení i odvolání účtu, starý tok bez profilu);
//  - „Dokončit smazání účtu“ – náhrobek po smazání účtu, kterému selhal
//    deleteUser (UpdateProfile.deleteAccount).
// Dřív takový účet aplikace při každém přihlášení odhlásila s hláškou
// „napište správci“ a opravit ho šlo jen ve Firebase konzoli.
// 3. kolo revize: výpadek sítě v kroku uživatele neodhlásí – krok zůstane
// s hláškou (nález 4); fokus a posun na úvod nového kroku, důsledky výběru
// přes aria-describedby (nález 5); zmizelý vybraný import se ohlásí, ne
// tiše nový člen (nález 3); rok narození v popisku kandidáta a z kroku
// Doplňte údaje jde účet smazat (nálezy 1 a 6).
// 4. kolo revize (nález 2) – sdílené zařízení: po odhlášení, které spustí
// aplikace sama, nezůstane ve formulářích heslo ani osobní údaje dalšímu
// u zařízení (po registraci a při čekání na ověření e-mailu zůstává jen
// e-mail, krok pro jiný účet začíná prázdný). Chyba dokončení registrace
// odhlásí, vymaže cache Firestore (rozpracovaný profil, importy se stejným
// e-mailem) a načte přihlášení znovu – hláška přežije v sessionStorage.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";
import logo from "./assets/lksLogo.png";
import { auth, clearFirestoreCache, restartApp } from "./firebase";
import GdprModal from "./GdprModal";
import { resetPassword } from "./auth";
import {
  accountDeletedRemembered,
  deleteAccount,
  forgetAccountDeleted,
  rememberAccountDeleted,
} from "./UpdateProfile";
import { toTitleCase } from "./utils/nameUtils";
import { useAuth } from "./utils/useAuth";
import { firebaseErrorMessage, isRetryableError } from "./utils/firebaseErrors";
import { CONTACT_EMAIL } from "./utils/contact";
import { reportError, reportWarning } from "./utils/reportError";
import {
  RESEND_VERIFICATION_COOLDOWN_SECONDS,
  VERIFY_EMAIL_SETTINGS,
} from "./utils/emailVerification";
import {
  completeRegistration,
  completionErrorMessage,
  pendingSaveErrorMessage,
  savePendingProfile,
  waitForSignedInFirestore,
  type LegacyCandidate,
  type LockedField,
  type RegistrationChoice,
} from "./utils/completeRegistration";
import { scrollIntoMain, useAppHeader } from "./ui/AppShell";
import { useToast } from "./ui/Toast";
import { Section } from "./ui/Section";
import { Field, type FieldControlProps } from "./ui/Field";
import { PhoneInput, validatePhone } from "./ui/PhoneInput";
import { LoadingState } from "./ui/AsyncState";

/** Prodleva mezi žádostmi o obnovu hesla – brání spamování e-mailů. */
const RESET_COOLDOWN_SECONDS = 60;
/** Stejná prodleva pro opakované odeslání ověřovacího e-mailu. */
const RESEND_COOLDOWN_SECONDS = RESEND_VERIFICATION_COOLDOWN_SECONDS;

const MIN_BIRTH_YEAR = 1920;
/** Minimum Firebase Auth (jinak auth/weak-password). */
const MIN_PASSWORD_LENGTH = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormField =
  | "email"
  | "password"
  | "firstName"
  | "lastName"
  | "birthYear"
  | "gender"
  | "nationality"
  | "customNationality"
  | "phone"
  | "consent"
  | "choice"
  | "deletePassword";

type FormErrors = Partial<Record<FormField, string>>;

type BusyAction =
  | "login"
  | "register"
  | "reset"
  | "resend"
  | "details"
  | "confirm"
  | "confirmNew"
  | "finishDelete"
  | "logout"
  /** Po chybě dokončení: cache je vymazaná, stránka se načítá znovu – vše zamčené. */
  | "restart";

/** Trvalá zpráva nad formulářem (po registraci / při neověřeném e-mailu / po smazání účtu). */
type Notice =
  | { kind: "registered"; email: string; mailSent: boolean }
  | { kind: "unverified"; email: string }
  | { kind: "deleted" };

/**
 * Přihlášený ověřený účet, kterému chybí profil a dokončení registrace
 * potřebuje odpověď – místo formulářů přihlášení se ukáže tento krok.
 * Uživatel zůstává přihlášený (dřív ho aplikace odhlásila).
 */
type Unfinished =
  /** Bez profilu i rozpracovaného profilu – formulář „Doplňte údaje“ (nález 10). */
  | { kind: "details"; user: User }
  /**
   * Importy se stejným e-mailem, ale jiným jménem nebo rokem narození –
   * „Jste to vy?“ (nález 8). gone = import vybraný odpovědí „Jsem to já“,
   * který mezitím zmizel (nález 3 3. kola); candidates pak mohou být prázdné.
   */
  | { kind: "confirm"; user: User; candidates: LegacyCandidate[]; gone?: LegacyCandidate }
  /**
   * Smazání účtu: náhrobek nedokončeného smazání – „Dokončit smazání účtu“
   * (nálezy 10, 12) –, nebo volba „Chci účet smazat“ v kroku Doplňte údaje
   * (requested, nález 6 3. kola; jde se k němu vrátit).
   */
  | { kind: "deletion"; user: User; requested?: boolean };

/** Výsledek kontroly profilu (ensureMemberProfile). */
type ProfileOutcome =
  /**
   * Profil je (nebo start bez sítě) – dál do aplikace. notice: hláška po
   * převodu importu, který ponechal jiný rok narození / pohlaví, než uvedla
   * registrace (overruledNotice).
   */
  | { kind: "ready"; notice?: string }
  /**
   * Chyba – uživatel je odhlášený, hláška patří k formuláři přihlášení.
   * restarting: cache Firestore je vymazaná a stránka se načítá znovu
   * (hláška čeká v sessionStorage); false = odhlášení se nepovedlo, nové
   * načtení by chybu jen opakovalo dokola.
   */
  | { kind: "failed"; message: string; restarting: boolean }
  /** Výpadek sítě v kroku dokončení – krok zůstává, hláška patří do něj. */
  | { kind: "retry"; message: string }
  | Unfinished;

/**
 * Odkud přichází kontrola profilu – podle toho se řeší výpadek sítě:
 *  - "startup": trvající přihlášení při startu aplikace (efekt) – bez
 *    signálu (střelnice) a s prázdnou cache by se člen odhlásil a offline
 *    už nepřihlásil; pustí se dál jako dřív, profil se ověří příště;
 *  - "login": právě proběhlé přihlášení – odhlášení a hláška u formuláře
 *    (po vymazání cache a novém načtení stránky – nález 2 4. kola; přihlásit
 *    se jde hned znovu);
 *  - "step": odpověď v kroku dokončení („Jste to vy?“, „Doplňte údaje“) –
 *    krok zůstává s hláškou a jde zopakovat bez nového přihlášení
 *    (nález 4 3. kola).
 */
type CheckMode = "startup" | "login" | "step";

/** Volba „Nejsem nikdo z nich – jsem nový člen“ v dotazu na import. */
const NEW_MEMBER_CHOICE = "__new__";

/** Poznámka pod rokem narození a pohlavím (registrace, Doplňte údaje). */
const LOCKED_PERSONAL_NOTE_ID = "regLockedNote";

/** Pole přihlášení v pořadí na obrazovce → id prvku (fokus na první chybu). */
const LOGIN_FIELDS: Array<[FormField, string]> = [
  ["email", "loginEmail"],
  ["password", "loginPassword"],
];

/** Pole registrace v pořadí na obrazovce → id prvku. */
const REGISTER_FIELDS: Array<[FormField, string]> = [
  ["firstName", "regFirstName"],
  ["lastName", "regLastName"],
  ["birthYear", "regBirthYear"],
  ["gender", "regGender"],
  ["nationality", "regNationality"],
  ["customNationality", "regNationalityOther"],
  ["email", "regEmail"],
  ["phone", "regPhone"],
  ["password", "regPassword"],
  ["consent", "regConsent"],
];

/** Pole „Doplňte údaje“ – jako registrace, bez e-mailu (je z účtu) a hesla. */
const DETAILS_FIELDS = REGISTER_FIELDS.filter(
  ([field]) => field !== "email" && field !== "password"
);

/** Výběr v dotazu na import (víc kandidátů). */
const CHOICE_FIELDS: Array<[FormField, string]> = [["choice", "regChoice-0"]];

/** Heslo pro dokončení smazání účtu. */
const DELETE_FIELDS: Array<[FormField, string]> = [["deletePassword", "finishDeletePassword"]];

/** E-mail pro Auth i Firestore: bez okrajových mezer, malými písmeny. */
const normalizeEmail = (value: unknown): string =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

/** Kód chyby Firebase / vlastní chyby, jinak "". */
const errorCodeOf = (err: unknown): string => {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : "";
};

/** Odhlášení, jehož selhání nemá shodit tok (jen stopa v diagnostice). */
const signOutQuietly = async (where: string): Promise<void> => {
  try {
    await signOut(auth);
  } catch (err) {
    reportWarning(where, err);
  }
};

/**
 * Hláška chyby dokončení registrace přes nové načtení stránky
 * (sessionStorage, nález 2 4. kola): po chybě se kvůli vymazání cache
 * stránka načte znovu (restartApp) a stav stránky by zmizel. Stejně jako
 * potvrzení smazání účtu (UpdateProfile.rememberAccountDeleted); nová
 * stránka ji ukáže jednou a zapomene.
 */
const LOGIN_FAILURE_KEY = "lks-login-failure";

function rememberLoginFailure(message: string): void {
  try {
    window.sessionStorage.setItem(LOGIN_FAILURE_KEY, message);
  } catch {
    // Zakázané úložiště – stránka se načte bez hlášky (odhlášení proběhlo).
  }
}

function rememberedLoginFailure(): string | null {
  try {
    return window.sessionStorage.getItem(LOGIN_FAILURE_KEY);
  } catch {
    return null;
  }
}

function forgetLoginFailure(): void {
  try {
    window.sessionStorage.removeItem(LOGIN_FAILURE_KEY);
  } catch {
    // Zakázané úložiště – není co zapomenout.
  }
}

/**
 * Import v dotazu „Jste to vy?“ do věty „evidujeme člena …“: „Jan Novák,
 * nar. 2010 (č. 031)“ – rok narození rozliší stejná jména v rodině (nález 1
 * 3. kola); bez jména „č. 031 (bez uvedeného jména)“ (dřív „člena člen bez
 * uvedeného jména“, nález 6 3. kola).
 */
const candidateDescription = (candidate: LegacyCandidate): string => {
  const born = candidate.birthYear ? `nar. ${candidate.birthYear}` : "";
  const number = candidate.sequenceNumber ? `č. ${candidate.sequenceNumber}` : "";
  if (candidate.name) {
    const named = born ? `${candidate.name}, ${born}` : candidate.name;
    return number ? `${named} (${number})` : named;
  }
  const details = ["bez uvedeného jména", born].filter(Boolean).join(", ");
  return number ? `${number} (${details})` : details;
};

/** Možnost ve výběru z víc importů: „Jan Novák, nar. 2010 (č. 031)“, „Člen č. 031 (bez uvedeného jména)“. */
const candidateOption = (candidate: LegacyCandidate): string =>
  candidate.name ? candidateDescription(candidate) : `Člen ${candidateDescription(candidate)}`;

/**
 * Převod importu ponechal rok narození / pohlaví ze záznamu klubu, ačkoli
 * registrace uvedla jiné (rozhodnutí majitele 12. 10. 2026) – i u převodu
 * bez dotazu podle jména a roku, kde se může lišit pohlaví.
 */
const overruledNotice = (fields: LockedField[]): string => {
  const both = fields.length > 1;
  const subject = both
    ? "Rok narození a pohlaví"
    : fields[0] === "birthYear"
    ? "Rok narození"
    : "Pohlaví";
  return `${subject} jsme převzali ze záznamu klubu – v registraci ${
    both ? "byly jiné" : "bylo jiné"
  }. Pohlaví a rok narození může změnit jen správce.`;
};

/** „Záznam č. 050 už v evidenci není – mohl ho upravit správce.“ (nález 3 3. kola) */
const goneRecordText = (gone: LegacyCandidate): string => {
  const record = gone.sequenceNumber
    ? `Záznam č. ${gone.sequenceNumber}`
    : gone.name
    ? `Záznam „${gone.name}“`
    : "Vybraný záznam";
  return `${record} už v evidenci není – mohl ho upravit správce.`;
};

/**
 * Přihlášený uživatel s ověřeným e-mailem: dokončí registraci – profil
 * users/{uid} vzniká až teď (převod importu, nebo nový člen); existující
 * profil jen ověří. choice = odpověď na dotaz „Jste to vy?“, asked = jeho
 * kandidáti (zmizelý vybraný import se podle nich pojmenuje).
 *  - Import s jiným jménem nebo rokem → krok „confirm“ (nic se nezapsalo).
 *  - Bez profilu i rozpracovaného profilu → krok „details“, s náhrobkem
 *    smazaného profilu krok „deletion“; uživatel zůstává přihlášený.
 *  - Výpadek sítě: podle mode (CheckMode) dál do aplikace, krok zůstává
 *    („retry“), nebo jako jiná chyba.
 *  - Jiná chyba → odhlášení a hláška („failed“); rozpracovaný profil
 *    zůstává, takže příští přihlášení to zkusí znovu. Po odhlášení se
 *    vymaže cache Firestore a přihlášení se načte znovu (nález 2 4. kola):
 *    dokončení mohlo načíst rozpracovaný profil a importy se stejným
 *    e-mailem – i ostatních členů rodiny (rok narození, telefon, ZP,
 *    členství) – a na sdíleném zařízení by v cache zůstaly. Hláška přežije
 *    nové načtení v sessionStorage.
 */
async function ensureMemberProfile(
  user: User,
  mode: CheckMode,
  choice?: RegistrationChoice,
  asked: LegacyCandidate[] = []
): Promise<ProfileOutcome> {
  try {
    const result = await completeRegistration(user, choice);
    if (result.status === "needs-confirmation") {
      const goneId = result.goneLegacyId;
      const gone = goneId
        ? asked.find((c) => c.id === goneId) || {
            id: goneId,
            name: "",
            birthYear: null,
            sequenceNumber: null,
          }
        : undefined;
      return { kind: "confirm", user, candidates: result.candidates, gone };
    }
    if (result.status === "transferred" && result.overruled && result.overruled.length > 0) {
      return { kind: "ready", notice: overruledNotice(result.overruled) };
    }
    return { kind: "ready" };
  } catch (err) {
    const code = errorCodeOf(err);
    if (code === "lks/account-deleted") {
      reportWarning("AuthPage.completeRegistration.deleted", err, { uid: user.uid });
      return { kind: "deletion", user };
    }
    if (code === "lks/registration-incomplete") {
      reportWarning("AuthPage.completeRegistration.incomplete", err, { uid: user.uid });
      return { kind: "details", user };
    }
    if (mode === "startup" && isRetryableError(err)) {
      reportWarning("AuthPage.completeRegistration.offline", err, { uid: user.uid });
      return { kind: "ready" };
    }
    if (mode === "step" && isRetryableError(err)) {
      reportWarning("AuthPage.completeRegistration.retry", err, { uid: user.uid });
      return { kind: "retry", message: completionErrorMessage(err) };
    }
    reportError("AuthPage.completeRegistration", err, { uid: user.uid });
    const message = completionErrorMessage(err);
    await signOutQuietly("AuthPage.completeRegistration.signOut");
    const current = auth.currentUser;
    if (current && current.uid === user.uid) {
      // Odhlášení se nepovedlo – nové načtení by účet zase přihlásilo
      // a chyba by se opakovala dokola. Jen hláška jako dřív.
      return { kind: "failed", message, restarting: false };
    }
    // Jako „Odhlásit se“ v kroku dokončení (handleUnfinishedSignOut):
    // vymazat cache a přihlášení načíst znovu (bez sítě až po obnovení
    // spojení – do té doby zůstane zobrazená hláška).
    rememberLoginFailure(message);
    await clearFirestoreCache();
    restartApp("/login");
    return { kind: "failed", message, restarting: true };
  }
}

const validateEmail = (value: string): string | undefined => {
  if (!value) return "Zadejte e-mail.";
  if (!EMAIL_PATTERN.test(value)) return "Zadejte e-mail ve tvaru jmeno@domena.cz.";
  return undefined;
};

/**
 * Cíl po přihlášení: stránka, ze které poslal RequireAuth (state.from), jinak
 * /user. Jen cesta uvnitř aplikace – nikdy zpět na přihlášení.
 */
const redirectTargetFrom = (state: unknown): string => {
  const from =
    state && typeof state === "object" ? (state as { from?: unknown }).from : undefined;
  if (
    typeof from === "string" &&
    from.charAt(0) === "/" &&
    from.charAt(1) !== "/" &&
    from !== "/" &&
    from !== "/login"
  ) {
    return from;
  }
  return "/user";
};

/**
 * Fokus na pole s chybou. Vycentrovat (ne block:'start' ani prosté focus()),
 * aby pole nezajelo pod fixní hlavičku AppShellu.
 */
const focusField = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ block: "center" });
  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
};

/** Odpočet v sekundách (0 = neběží); časovač se uklidí i při odchodu ze stránky. */
function useCountdown(): [number, (seconds: number) => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return undefined;
    const timer = setTimeout(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearTimeout(timer);
  }, [left]);
  return [left, setLeft];
}

// ─────────────────────────────────────────────────────────────
//  Heslo s tlačítkem „zobrazit / skrýt"
// ─────────────────────────────────────────────────────────────

interface PasswordControlProps extends FieldControlProps {
  value: string;
  onValueChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
  visible: boolean;
  onToggleVisible: () => void;
}

/**
 * Field komponentu naklonuje a doplní id, aria-* a is-invalid – ty patří
 * vstupu, ne obalu input-group (stejný vzor jako PhoneInput).
 */
const PasswordControl: React.FC<PasswordControlProps> = ({
  id,
  className,
  required,
  "aria-describedby": describedBy,
  "aria-invalid": ariaInvalid,
  value,
  onValueChange,
  autoComplete,
  visible,
  onToggleVisible,
}) => {
  const toggleLabel = visible ? "Skrýt heslo" : "Zobrazit heslo";
  return (
    <div className="input-group">
      <input
        id={id}
        name="password"
        type={visible ? "text" : "password"}
        className={className ? `form-control ${className}` : "form-control"}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        required={required}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={onToggleVisible}
        aria-label={toggleLabel}
        aria-controls={id}
        title={toggleLabel}
      >
        <i className={`bi ${visible ? "bi-eye-slash" : "bi-eye"}`} aria-hidden="true" />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  AuthPage
// ─────────────────────────────────────────────────────────────

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, ready } = useAuth();
  const { showToast } = useToast();
  const { setHeader } = useAppHeader();

  const [isLogin, setIsLogin] = useState(true);
  // E-mail a heslo sdílí přihlášení i registrace – po registraci zůstane
  // vyplněný jen e-mail pro přihlášení po ověření; heslo a osobní údaje se
  // vymažou (sdílené zařízení, nález 2 4. kola – forgetEnteredData).
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthYear, setBirthYear] = useState<number | undefined>(undefined);
  const [gender, setGender] = useState("");
  const [nationality, setNationality] = useState("");
  const [customNationality, setCustomNationality] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  // Chyby Firebase mimo konkrétní pole (síť, limit pokusů…) – alert nad tlačítkem,
  // ne sticky toast, který by zůstal viset i po úspěšném přihlášení.
  // Po chybě dokončení registrace se stránka načte znovu – hláška přichází
  // přes sessionStorage (rememberLoginFailure).
  const [formError, setFormError] = useState<string | null>(() => rememberedLoginFailure());
  // Po smazání účtu se stránka načte znovu (vymazání cache) – potvrzení
  // přichází přes sessionStorage (UpdateProfile.rememberAccountDeleted).
  const [notice, setNotice] = useState<Notice | null>(() =>
    accountDeletedRemembered() ? { kind: "deleted" } : null
  );
  // Krok, který dokončí registraci nebo smazání účtu (místo formulářů).
  const [unfinished, setUnfinished] = useState<Unfinished | null>(null);
  const [choice, setChoice] = useState("");
  const [deletePassword, setDeletePassword] = useState("");

  // Běžící akce: spinner v jejím tlačítku, ostatní tlačítka zamčená.
  // Ref chrání před dvojím odesláním dřív, než se stav překreslí.
  const [busy, setBusy] = useState<BusyAction | null>(null);
  const busyRef = useRef<BusyAction | null>(null);
  // Stránka se po vymazání cache načítá znovu (chyba dokončení) – Firestore
  // je ukončený, akce se už neodemknou.
  const restartingRef = useRef(false);
  // Současný krok dokončení (pro applyOutcome – krok jiného účtu začíná
  // s prázdným formulářem).
  const unfinishedRef = useRef<Unfinished | null>(null);
  unfinishedRef.current = unfinished;

  const [resetCooldown, startResetCooldown] = useCountdown();
  const [resendCooldown, startResendCooldown] = useCountdown();

  // Než AuthProvider poprvé vyhodnotí přihlášení, ukazuje se spinner – vrácený
  // uživatel (restart aplikace ve WebView) tak nevidí na okamžik formulář.
  const [authChecked, setAuthChecked] = useState(ready);

  const noticeRef = useRef<HTMLDivElement>(null);
  const formErrorRef = useRef<HTMLDivElement>(null);
  const stepIntroRef = useRef<HTMLDivElement>(null);
  const gdprRef = useRef<HTMLDivElement>(null);

  const buildTime = process.env.REACT_APP_BUILD_TIME || "neznámá";
  const currentYear = new Date().getFullYear();
  const redirectTarget = redirectTargetFrom(location.state);

  useLayoutEffect(() => {
    let title = isLogin ? "Přihlášení" : "Registrace";
    if (unfinished) {
      title = unfinished.kind === "deletion" ? "Smazání účtu" : "Dokončení registrace";
    }
    setHeader({ title });
  }, [setHeader, isLogin, unfinished]);

  useEffect(() => {
    if (ready) setAuthChecked(true);
  }, [ready]);

  // Hláška z minulé stránky (chyba dokončení před novým načtením) se ukáže
  // jednou – další návštěva přihlášení už ji nemá.
  useEffect(() => {
    forgetLoginFailure();
  }, []);

  /**
   * Vymaže heslo a osobní údaje z formulářů (registrace i „Doplňte údaje“
   * sdílí tytéž stavy), heslo ke smazání účtu a výběr v dotazu. E-mail
   * zůstává. Volá se, když aplikace sama odhlásí nebo krok dokončení
   * patří jinému účtu – na sdíleném zařízení by je jinak viděl další
   * člověk („Zobrazit heslo“, předvyplněný krok; nález 2 4. kola).
   */
  const forgetEnteredData = useCallback(() => {
    setPassword("");
    setPasswordVisible(false);
    setFirstName("");
    setLastName("");
    setBirthYear(undefined);
    setGender("");
    setNationality("");
    setCustomNationality("");
    setPhone("");
    setConsent(false);
    setChoice("");
    setDeletePassword("");
  }, []);

  /**
   * Výsledek kontroly profilu: dál do aplikace, hláška u přihlášení
   * (uživatel je už odhlášený), hláška v současném kroku (výpadek sítě),
   * nebo krok, který registraci / smazání dokončí.
   */
  const applyOutcome = useCallback(
    (outcome: ProfileOutcome) => {
      if (outcome.kind === "ready") {
        // Toast žije v provideru – přežije přesměrování.
        if (outcome.notice) showToast(outcome.notice, "warning");
        navigate(redirectTarget, { replace: true });
        return;
      }
      if (outcome.kind === "failed") {
        // Odhlášený účet: krok zmizí i s vyplněnými údaji. Do nového
        // načtení stránky (bez sítě až po obnovení spojení) zůstane jen
        // hláška a vše je zamčené – Firestore je po vymazání cache ukončený.
        setUnfinished(null);
        forgetEnteredData();
        setErrors({});
        setFormError(outcome.message);
        if (outcome.restarting) {
          restartingRef.current = true;
          busyRef.current = "restart";
          setBusy("restart");
        }
        return;
      }
      if (outcome.kind === "retry") {
        // Krok i výběr zůstávají, uživatel je dál přihlášený – stačí to
        // zopakovat, až bude signál.
        setFormError(outcome.message);
        return;
      }
      // Krok pro jiný účet než ten současný (i první krok po přihlášení):
      // údaje ve formuláři patří někomu jinému – registraci rozepsané na
      // tomtéž zařízení, nebo předchozímu účtu. Návrat ke kroku téhož účtu
      // (Zpět ze smazání, další krok po odpovědi) je nechá.
      const previous = unfinishedRef.current;
      if (!previous || previous.user.uid !== outcome.user.uid) forgetEnteredData();
      setErrors({});
      setFormError(null);
      setChoice("");
      setDeletePassword("");
      setUnfinished(outcome);
    },
    [navigate, redirectTarget, forgetEnteredData, showToast]
  );

  // Přihlášený a ověřený uživatel sem nepatří (start aplikace, otevřený /login)
  // – po kontrole profilu dál. Bez profilu (přihlášení přerušené před
  // dokončením registrace) se registrace dokončí tady. Přihlášení a nové
  // odeslání ověřovacího e-mailu dokončují registraci samy (busy), efekt
  // jim nepřebíhá; stejně tak rozběhnutý krok dokončení (unfinished).
  // Neověřený zůstává na stránce, ať vidí hlášku.
  const verifiedUser = ready && !!user && user.emailVerified;
  // Během kontroly profilu spinner místo formuláře.
  const showSpinner = !authChecked || (verifiedUser && !unfinished);
  useEffect(() => {
    if (!verifiedUser || !user || busyRef.current || unfinished) return undefined;
    // Odhlášení (např. po chybě dokončení) už proběhlo, AuthProvider ho
    // jen ještě nepřekreslil – nic nespouštět.
    const current = auth.currentUser;
    if (!current || current.uid !== user.uid) return undefined;
    let active = true;
    ensureMemberProfile(user, "startup").then((outcome) => {
      // Chyba (odhlášení a nové načtení) platí vždy: odhlášení mezitím
      // efekt ukončí (user = null), hláška a zámek ale patří na stránku.
      if (active || outcome.kind === "failed") applyOutcome(outcome);
    });
    return () => {
      active = false;
    };
  }, [verifiedUser, user, unfinished, applyOutcome]);

  // Krok dokončení patří přihlášenému účtu – po odhlášení (i v jiné
  // záložce) nebo přepnutí účtu zmizí i s vyplněnými údaji (nález 2
  // 4. kola). Rozhoduje auth.currentUser: AuthProvider ohlásí čerstvé
  // přihlášení o chvíli později.
  useEffect(() => {
    if (!unfinished) return;
    const current = auth.currentUser;
    if (!current || current.uid !== unfinished.user.uid) {
      setUnfinished(null);
      forgetEnteredData();
    }
  }, [unfinished, ready, user, forgetEnteredData]);

  // Nová trvalá zpráva musí být vidět (registrace končí dole u tlačítka, které
  // po přepnutí na přihlášení zmizí) – odscrollovat a přesunout na ni fokus.
  useEffect(() => {
    const el = noticeRef.current;
    if (!notice || !el) return;
    scrollIntoMain(el);
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, [notice]);

  // Chyba mimo pole (síť, limit pokusů) se vykresluje u tlačítka odeslání –
  // u dlouhé registrace může být mimo obraz, proto stejný scroll + fokus.
  // Chyba dokončení registrace přijde ještě pod spinnerem – až formulář
  // naskočí (showSpinner).
  useEffect(() => {
    const el = formErrorRef.current;
    if (!formError || showSpinner || !el) return;
    scrollIntoMain(el);
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, [formError, showSpinner]);

  // Nový krok dokončení (po přihlášení, odpovědi, přepnutí na smazání) –
  // fokus a posun na jeho úvod. Formulář s fokusem na tlačítku mezitím
  // nahradil spinner, takže by fokus spadl na <body> a čtečka by krok
  // neohlásila (nález 5 3. kola). Stejně jako trvalá zpráva výše.
  useEffect(() => {
    const el = stepIntroRef.current;
    if (!unfinished || showSpinner || !el) return;
    scrollIntoMain(el);
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, [unfinished, showSpinner]);

  const startBusy = (action: BusyAction) => {
    busyRef.current = action;
    setBusy(action);
    // Potvrzení smazání účtu se ukazuje do další akce (i po novém načtení).
    forgetAccountDeleted();
  };

  const stopBusy = () => {
    // Stránka se načítá znovu (applyOutcome – chyba dokončení): zůstává zamčená.
    if (restartingRef.current) return;
    busyRef.current = null;
    setBusy(null);
  };

  /** Chyby Firebase, které patří ke konkrétnímu poli (heslo / e-mail); jinak null. */
  const authFieldErrors = (err: unknown): FormErrors | null => {
    const code = (err as { code?: unknown } | null)?.code;
    if (
      code === "auth/wrong-password" ||
      code === "auth/invalid-credential" ||
      code === "auth/missing-password"
    ) {
      return { password: firebaseErrorMessage(err) };
    }
    if (
      code === "auth/user-not-found" ||
      code === "auth/invalid-email" ||
      code === "auth/email-already-in-use" ||
      code === "auth/user-disabled"
    ) {
      return { email: firebaseErrorMessage(err) };
    }
    return null;
  };

  const clearError = (field: FormField) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  /** Chyby k polím + fokus na první chybné pole; true = formulář je v pořádku. */
  const applyErrors = (
    next: FormErrors,
    fields: Array<[FormField, string]>
  ): boolean => {
    setErrors(next);
    const first = fields.find(([field]) => next[field]);
    if (first) {
      focusField(first[1]);
      return false;
    }
    return true;
  };

  const switchMode = (toLogin: boolean) => {
    if (busyRef.current) return;
    setIsLogin(toLogin);
    setErrors({});
    setFormError(null);
    setNotice(null);
    forgetAccountDeleted();
    setPasswordVisible(false);
  };

  // GdprModal si otevírání řídí sama (bez props) a kreslí vlastní tlačítko.
  // Na stránce je jednou, mimo formuláře (původní verze měla tlačítko bez
  // type="button" – uvnitř <form> by odeslalo registraci a Enter v poli by
  // místo odeslání otevřel GDPR). Odkaz u souhlasu proto jeho tlačítko jen
  // „stiskne"; až GdprModal dostane řízené otevření (open/onClose), volat přímo.
  const openGdpr = () => {
    const trigger = gdprRef.current ? gdprRef.current.querySelector("button") : null;
    if (trigger) trigger.click();
    else scrollIntoMain(gdprRef.current);
  };

  // ── Přihlášení ─────────────────────────────────────────────

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    setFormError(null);
    e.preventDefault();
    if (busyRef.current) return;

    // Android klávesnice a autofill rády přidají mezeru za e-mail; Firebase
    // pak hlásí „uživatel neexistuje". Proto vždy ořez + malá písmena.
    const normalizedEmail = normalizeEmail(email);
    const valid = applyErrors(
      {
        email: validateEmail(normalizedEmail),
        password: password ? undefined : "Zadejte heslo.",
      },
      LOGIN_FIELDS
    );
    if (!valid) return;

    startBusy("login");
    try {
      // Přihlášení přežije restart aplikace i bez setPersistence: výchozí
      // úložiště (IndexedDB) je trvalé. setPersistence(LOCAL) ho dřív
      // přepínalo na localStorage jen v této záložce – ostatní otevřené
      // záložky se o přihlášení nedozvěděly a Firestore, který čte ze
      // serveru přes jednu z nich, posílal dotazy nepřihlášeně („nemáte
      // oprávnění", 4. 10. 2026).
      const credential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      if (!credential.user.emailVerified) {
        // Neověřený uživatel nesmí zůstat přihlášený; zpráva nabídne nové
        // odeslání ověřovacího e-mailu. Heslo ve formuláři nezůstane –
        // člověk odchází ověřit e-mail a na sdíleném zařízení by ho přečetl
        // další (nález 2 4. kola); nové odeslání si ho vyžádá.
        setNotice({
          kind: "unverified",
          email: credential.user.email || normalizedEmail,
        });
        await signOut(auth);
        setPassword("");
        setPasswordVisible(false);
        return;
      }

      // Ověřený e-mail: první přihlášení po registraci teprve založí profil
      // (převod importu nebo nový člen), u dalších se jen ověří, že je.
      // Nejdřív počkat, až přihlášení převezme i jiná otevřená záložka
      // (čtení ze serveru jdou přes ni) – jinak by dokončení skončilo
      // „nemáte oprávnění" a odhlásilo.
      setNotice(null);
      await waitForSignedInFirestore(credential.user.uid);
      applyOutcome(await ensureMemberProfile(credential.user, "login"));
    } catch (err) {
      const fieldErrors = authFieldErrors(err);
      if (fieldErrors) applyErrors(fieldErrors, LOGIN_FIELDS);
      else setFormError(firebaseErrorMessage(err, "Chyba při přihlášení."));
    } finally {
      stopBusy();
    }
  };

  // ── Registrace ─────────────────────────────────────────────

  /** Osobní údaje, telefon a souhlas – registrace i „Doplňte údaje“. */
  const validatePersonal = (): FormErrors => {
    const next: FormErrors = {};
    if (!firstName.trim()) next.firstName = "Vyplňte jméno.";
    if (!lastName.trim()) next.lastName = "Vyplňte příjmení.";
    if (birthYear === undefined) {
      next.birthYear = "Zadejte rok narození.";
    } else if (
      !Number.isInteger(birthYear) ||
      birthYear < MIN_BIRTH_YEAR ||
      birthYear > currentYear
    ) {
      next.birthYear = `Zadejte rok narození mezi ${MIN_BIRTH_YEAR} a ${currentYear}.`;
    }
    if (!gender) next.gender = "Vyberte pohlaví.";
    if (!nationality) {
      next.nationality = "Vyberte státní občanství.";
    } else if (nationality === "jiná" && !customNationality.trim()) {
      next.customNationality = "Vyplňte státní občanství.";
    }
    next.phone = validatePhone(phone, true);
    if (!consent) {
      next.consent =
        "Bez souhlasu se zpracováním osobních údajů se nelze zaregistrovat.";
    }
    return next;
  };

  const validateRegistration = (): FormErrors => {
    const next = validatePersonal();
    next.email = validateEmail(normalizeEmail(email));
    if (!password) {
      next.password = "Zadejte heslo.";
    } else if (password.length < MIN_PASSWORD_LENGTH) {
      next.password = `Heslo musí mít alespoň ${MIN_PASSWORD_LENGTH} znaků.`;
    }
    return next;
  };

  /**
   * Údaje z formuláře pro rozpracovaný profil (pendingProfiles) – stejná pole
   * jako dřív v users; profil z nich vznikne po ověření e-mailu. Jména vždy
   * Title Case (opraví VELKÁ PÍSMENA i vše malé).
   */
  const personalProfile = () => ({
    firstName: toTitleCase(firstName),
    lastName: toTitleCase(lastName),
    birthYear: Number(birthYear),
    gender: gender.toLowerCase(),
    nationality: nationality === "jiná" ? customNationality.trim() : nationality,
    phone,
  });

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    setFormError(null);
    e.preventDefault();
    if (busyRef.current) return;
    if (!applyErrors(validateRegistration(), REGISTER_FIELDS)) return;

    const normalizedEmail = normalizeEmail(email);
    const profile = { ...personalProfile(), email: normalizedEmail };
    const normalizedFirstName = profile.firstName;
    const normalizedLastName = profile.lastName;

    startBusy("register");

    let newUser: User | null = null;
    let step = "createUser";
    try {
      const credential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );
      newUser = credential.user;

      step = "updateProfile";
      await updateProfile(newUser, {
        displayName: `${normalizedFirstName} ${normalizedLastName}`,
      });

      // Jen rozpracovaný profil: Firebase Auth založí účet i s cizím
      // e-mailem, takže profil users/{uid} (pořadové číslo, převod
      // importovaného profilu se stejným e-mailem) vznikne až při prvním
      // přihlášení s ověřeným e-mailem (ensureMemberProfile). Cizí profily
      // se tady nečtou. E-mail = e-mail účtu (pravidla ho porovnávají
      // s tokenem).
      step = "savePending";
      await savePendingProfile(newUser.uid, {
        ...profile,
        email: newUser.email || normalizedEmail,
      });
    } catch (err) {
      if (newUser) {
        // Účet v Auth bez rozpracovaného profilu by registraci zablokoval:
        // znovu se registrovat nejde (e-mail je „použitý") a přihlášení by
        // profil nemělo z čeho založit. Rozpracovaný profil zapsaný není
        // (transakce), takže účet smažeme a registrace jde zopakovat.
        // Uživatel je přihlášený teprve chvíli, deleteUser tedy nepotřebuje
        // novou autentizaci.
        reportError("AuthPage.register", err, { step });
        try {
          await deleteUser(newUser);
        } catch (rollbackErr) {
          reportError("AuthPage.register.rollback", rollbackErr, {
            uid: newUser.uid,
          });
          // Účet v Auth zůstal bez rozpracovaného profilu (typicky výpadek
          // sítě) – opakování registrace skončí na „e-mail je již
          // zaregistrován“. Přihlášení ho ale dokončí: nejdřív ověření
          // e-mailu (nové odeslání ve zprávě o neověřeném e-mailu), pak krok
          // „Doplňte údaje“ (nález 10 2. kola). Neověřený účet nezůstává
          // přihlášený. Dál se pokračuje přihlášením – ve formuláři jako po
          // registraci zůstane jen e-mail (nález 2 4. kola).
          await signOutQuietly("AuthPage.register.rollbackSignOut");
          forgetEnteredData();
          setEmail(normalizedEmail);
          setErrors({});
          setIsLogin(true);
          setFormError(
            `Registrace se nedokončila, účet ale už vznikl. Až to půjde, přihlaste se ` +
              `tímto e-mailem a heslem – aplikace vás provede ověřením e-mailu ` +
              `a doplněním údajů. Pokud to nepůjde, napište nám prosím na ${CONTACT_EMAIL}.`
          );
          stopBusy();
          return;
        }
      }
      const fieldErrors = authFieldErrors(err);
      if (fieldErrors) applyErrors(fieldErrors, REGISTER_FIELDS);
      else if (step === "savePending") {
        // Účet se odvolal; permission-denied = pravidla bez pendingProfiles
        // (nález 9 2. kola) – srozumitelná hláška místo „nemáte oprávnění“.
        setFormError(pendingSaveErrorMessage(err, "Chyba při registraci."));
      } else setFormError(firebaseErrorMessage(err, "Chyba při registraci."));
      stopBusy();
      return;
    }

    // Ověřovací e-mail až po uložených údajích (dřív odcházel před zápisem
    // a odkaz z e-mailu mohl vést na účet bez údajů). Neodeslaný e-mail
    // registraci nezruší – zpráva nabídne nové odeslání.
    let mailSent = true;
    try {
      await sendEmailVerification(newUser, VERIFY_EMAIL_SETTINGS);
    } catch (err) {
      // Hlášku nese zpráva v obsahu (mailSent: false) s tlačítkem „Poslat znovu".
      mailSent = false;
      reportWarning("AuthPage.register.sendVerification", err);
    }

    // Neověřený účet nezůstává přihlášený (stejně jako po přihlášení bez
    // ověření); registraci dokončí první přihlášení po ověření e-mailu.
    // Ve formuláři zůstane jen e-mail pro přihlášení (nález 2 4. kola):
    // heslo („Zobrazit heslo“) a osobní údaje by na sdíleném zařízení
    // přečetl další člověk. Nové odeslání ověřovacího e-mailu si heslo
    // vyžádá („Pro nové odeslání zadejte heslo.“).
    await signOutQuietly("AuthPage.register.signOut");

    forgetEnteredData();
    setEmail(normalizedEmail);
    setErrors({});
    setIsLogin(true);
    setNotice({ kind: "registered", email: normalizedEmail, mailSent });
    stopBusy();
  };

  // ── Obnova hesla ───────────────────────────────────────────

  const handleResetPassword = async () => {
    setFormError(null);
    if (busyRef.current || resetCooldown > 0) return;

    const normalizedEmail = normalizeEmail(email);
    const valid = applyErrors(
      {
        email: normalizedEmail
          ? validateEmail(normalizedEmail)
          : "Zadejte e-mail, na který pošleme odkaz pro změnu hesla.",
      },
      LOGIN_FIELDS
    );
    if (!valid) return;

    startBusy("reset");
    try {
      await resetPassword(normalizedEmail); // centralizovaná funkce z auth.ts
      startResetCooldown(RESET_COOLDOWN_SECONDS);
      showToast(
        `Odkaz pro změnu hesla jsme poslali na ${normalizedEmail}.`,
        "success"
      );
    } catch (err) {
      const fieldErrors = authFieldErrors(err);
      if (fieldErrors) applyErrors(fieldErrors, LOGIN_FIELDS);
      else {
        setFormError(
          firebaseErrorMessage(err, "Chyba při odesílání e-mailu pro změnu hesla.")
        );
      }
    } finally {
      stopBusy();
    }
  };

  // ── Nové odeslání ověřovacího e-mailu ──────────────────────

  const handleResendVerification = async () => {
    setFormError(null);
    if (busyRef.current || resendCooldown > 0) return;

    let current = auth.currentUser;
    const normalizedEmail = normalizeEmail(email);
    // Účet přihlásila až tahle akce – při chybě se zase odhlásí.
    const signedInHere = !current;

    // Po odhlášení (běžný stav) je potřeba krátké přihlášení e-mailem a heslem.
    if (!current) {
      const valid = applyErrors(
        {
          email: validateEmail(normalizedEmail),
          password: password ? undefined : "Pro nové odeslání zadejte heslo.",
        },
        LOGIN_FIELDS
      );
      if (!valid) return;
    }

    startBusy("resend");
    try {
      if (current) {
        // Přihlášený neověřený účet (např. relace ze dřívější verze, která
        // po registraci neodhlašovala) – mohl si e-mail mezitím ověřit,
        // proto čerstvý stav ze serveru.
        await current.reload();
      } else {
        const credential = await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );
        current = credential.user;
      }

      if (current.emailVerified) {
        // Už ověřeno – žádný další e-mail: dokončit registraci (jako
        // přihlášení, i s čekáním na ostatní záložky) a dovnitř; při chybě
        // hláška a odhlášení.
        setNotice(null);
        await waitForSignedInFirestore(current.uid);
        applyOutcome(await ensureMemberProfile(current, "login"));
        return;
      }

      await sendEmailVerification(current, VERIFY_EMAIL_SETTINGS);
      startResendCooldown(RESEND_COOLDOWN_SECONDS);
      showToast(
        `Ověřovací e-mail jsme poslali znovu na ${current.email || normalizedEmail}.`,
        "success"
      );

      // Neověřený uživatel nemá zůstat přihlášený – ani heslo ve formuláři
      // (člověk odchází ověřit e-mail; nález 2 4. kola).
      await signOut(auth);
      setPassword("");
      setPasswordVisible(false);
    } catch (err) {
      reportWarning("AuthPage.resendVerification", err);
      // Selhalo až odeslání (limit pokusů, výpadek sítě) po přihlášení touto
      // akcí: neověřený účet nezůstane přihlášený ani heslo ve formuláři
      // (stejně jako po úspěšném odeslání – sdílené zařízení).
      const leftSignedIn = signedInHere && auth.currentUser !== null;
      if (leftSignedIn) {
        await signOutQuietly("AuthPage.resendVerification.signOut");
        setPassword("");
        setPasswordVisible(false);
      }
      const fieldErrors = authFieldErrors(err);
      if (fieldErrors) applyErrors(fieldErrors, LOGIN_FIELDS);
      else {
        const message = firebaseErrorMessage(err, "Ověřovací e-mail se nepodařilo odeslat.");
        setFormError(
          leftSignedIn ? `${message} Zkuste to za chvíli znovu – heslo zadejte znovu.` : message
        );
      }
    } finally {
      stopBusy();
    }
  };

  // ── Dokončení registrace / smazání (přihlášený účet bez profilu) ──

  /**
   * „Doplňte údaje“: údaje do rozpracovaného profilu (jako registrace, e-mail
   * z účtu) a hned dokončení registrace – převod importu podle ověřeného
   * e-mailu funguje i tady. Výpadek sítě při dokončení krok nechá
   * (uložené údaje se odešlou znovu).
   */
  const handleDetails = async (e: React.FormEvent<HTMLFormElement>) => {
    setFormError(null);
    e.preventDefault();
    if (busyRef.current || !unfinished) return;
    if (!applyErrors(validatePersonal(), DETAILS_FIELDS)) return;
    const account = unfinished.user;

    startBusy("details");
    try {
      await savePendingProfile(account.uid, {
        ...personalProfile(),
        email: account.email || "",
      });
    } catch (err) {
      reportError("AuthPage.details.savePending", err, { uid: account.uid });
      setFormError(pendingSaveErrorMessage(err, "Údaje se nepodařilo uložit."));
      stopBusy();
      return;
    }
    // Výsledek ještě s běžící akcí – efekt pro přihlášeného nesmí mezitím
    // spustit vlastní dokončení.
    applyOutcome(await ensureMemberProfile(account, "step"));
    stopBusy();
  };

  /**
   * Odpověď na „Jste to vy?“ – převést vybraný import, nebo nový člen.
   * Zmizelý vybraný import vrátí dotaz se jménem toho záznamu (gone).
   */
  const answerCandidates = async (answer: RegistrationChoice) => {
    setFormError(null);
    if (busyRef.current || !unfinished) return;
    const account = unfinished.user;
    const asked = unfinished.kind === "confirm" ? unfinished.candidates : [];
    startBusy(answer.kind === "new" ? "confirmNew" : "confirm");
    applyOutcome(await ensureMemberProfile(account, "step", answer, asked));
    stopBusy();
  };

  /**
   * „Chci účet smazat“ v kroku Doplňte údaje (nález 6 3. kola: účet bez
   * profilu i bez náhrobku – např. smazání starou verzí aplikace, které
   * nedoběhlo – jinak smazat nešel) a návrat zpět. Smazání pak jako
   * „Dokončit smazání účtu“; vyplněné údaje zůstávají.
   */
  const switchUnfinished = (kind: "details" | "deletion") => {
    if (busyRef.current || !unfinished) return;
    applyOutcome(
      kind === "deletion"
        ? { kind: "deletion", user: unfinished.user, requested: true }
        : { kind: "details", user: unfinished.user }
    );
  };

  /** Víc kandidátů: výběr ze seznamu (formulář). */
  const handleChoice = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busyRef.current) return;
    if (!applyErrors({ choice: choice ? undefined : "Vyberte jednu z možností." }, CHOICE_FIELDS)) {
      return;
    }
    answerCandidates(
      choice === NEW_MEMBER_CHOICE ? { kind: "new" } : { kind: "transfer", legacyId: choice }
    );
  };

  /**
   * „Dokončit smazání účtu“: deleteAccount bez profilu (náhrobek i profil už
   * jsou pryč – zbývají doklady a účet; po „Chci účet smazat“ se náhrobek
   * teprve zapíše), pak jako po smazání v Úpravě profilu vymazání cache
   * a nové načtení s potvrzením.
   */
  const handleFinishDeletion = async (e: React.FormEvent<HTMLFormElement>) => {
    setFormError(null);
    e.preventDefault();
    if (busyRef.current || !unfinished) return;
    const valid = applyErrors(
      { deletePassword: deletePassword ? undefined : "Zadejte heslo." },
      DELETE_FIELDS
    );
    if (!valid) return;
    const account = unfinished.user;

    startBusy("finishDelete");
    const result = await deleteAccount(account, deletePassword);
    if (result.ok) {
      // Akce zůstává běžet – stránka se po vymazání cache načte znovu.
      rememberAccountDeleted();
      setUnfinished(null);
      setDeletePassword("");
      setNotice({ kind: "deleted" });
      await clearFirestoreCache();
      restartApp("/login");
      return;
    }
    stopBusy();
    if ("passwordError" in result) {
      applyErrors({ deletePassword: result.passwordError }, DELETE_FIELDS);
    } else {
      setFormError(result.message);
    }
  };

  /**
   * Odhlášení z kroku dokončení (sdílené zařízení, jiný účet): jako
   * odhlášení v Profilu i s vymazáním cache Firestore – jsou v ní
   * rozpracovaný profil a importy se stejným e-mailem. Vyplněné údaje
   * zmizí hned (bez sítě se stránka načte znovu až po obnovení spojení).
   */
  const handleUnfinishedSignOut = async () => {
    if (busyRef.current) return;
    startBusy("logout");
    await signOutQuietly("AuthPage.unfinished.signOut");
    setUnfinished(null);
    forgetEnteredData();
    await clearFirestoreCache();
    restartApp("/login");
  };

  // ── Render ─────────────────────────────────────────────────

  const spinner = (
    <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
  );

  const renderNotice = (n: Notice) => {
    if (n.kind === "deleted") {
      return (
        <div
          ref={noticeRef}
          className="alert alert-success mb-3"
          role="status"
          tabIndex={-1}
        >
          <p className="fw-semibold mb-1">
            <i className="bi bi-check-circle me-2" aria-hidden="true" />
            Účet byl smazán
          </p>
          <p className="mb-0">Přihlašovací účet i údaje profilu jsou smazané.</p>
        </div>
      );
    }
    const ok = n.kind === "registered" && n.mailSent;
    let icon: string;
    let title: string;
    let text: string;
    if (n.kind === "registered" && n.mailSent) {
      icon = "bi-envelope-check";
      title = "Účet je vytvořený – zkontrolujte e-mail";
      text = `Na adresu ${n.email} jsme poslali ověřovací e-mail. Otevřete odkaz v něm a pak se tady přihlaste – registrace se dokončí při prvním přihlášení. Pokud e-mail nedorazí, podívejte se i do složky Spam.`;
    } else if (n.kind === "registered") {
      // Heslo se po registraci z formuláře maže (nález 2 4. kola) – nové
      // odeslání si ho vyžádá.
      icon = "bi-exclamation-triangle";
      title = "Účet je vytvořený, ověřovací e-mail ale neodešel";
      text = `Pošlete si ho prosím znovu – zadejte heslo do přihlášení níže a stiskněte „Poslat ověřovací e-mail znovu“. Bez ověření adresy ${n.email} se nepůjde přihlásit a registrace se nedokončí.`;
    } else {
      icon = "bi-envelope-exclamation";
      title = "E-mail zatím není ověřený";
      text = `Otevřete odkaz z ověřovacího e-mailu, který jsme poslali na ${n.email}, a pak se přihlaste znovu. Pokud e-mail nedorazil (zkontrolujte i Spam), zadejte heslo a pošlete si ho znovu.`;
    }

    return (
      <div
        ref={noticeRef}
        className={`alert ${ok ? "alert-success" : "alert-warning"} mb-3`}
        role="status"
        tabIndex={-1}
      >
        <p className="fw-semibold mb-1">
          <i className={`bi ${icon} me-2`} aria-hidden="true" />
          {title}
        </p>
        <p className="mb-3">{text}</p>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleResendVerification}
          disabled={busy !== null || resendCooldown > 0}
        >
          {busy === "resend" ? (
            spinner
          ) : (
            <i className="bi bi-arrow-repeat me-2" aria-hidden="true" />
          )}
          {resendCooldown > 0
            ? `Poslat ověřovací e-mail znovu (${resendCooldown} s)`
            : "Poslat ověřovací e-mail znovu"}
        </button>
      </div>
    );
  };

  // Chyba mimo pole – vždy jen v jednom (zobrazeném) formuláři.
  const formErrorAlert = formError ? (
    <div ref={formErrorRef} className="alert alert-danger" role="alert" tabIndex={-1}>
      {formError}
    </div>
  ) : null;

  const loginForm = (
    <form noValidate onSubmit={handleLogin} autoComplete="on" method="post">
      {/* Skrytá pole pro správce hesel / předvyplnění ve WebView (beze změny). */}
      <input
        type="text"
        name="username"
        autoComplete="username"
        style={{ display: "none" }}
      />
      <input
        type="password"
        name="password"
        autoComplete="current-password"
        style={{ display: "none" }}
      />

      <Section title="Přihlašovací údaje" icon="bi-box-arrow-in-right">
        <Field id="loginEmail" label="E-mail" error={errors.email}>
          <input
            type="email"
            name="email"
            className="form-control"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearError("email");
            }}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </Field>

        <Field id="loginPassword" label="Heslo" error={errors.password}>
          <PasswordControl
            value={password}
            onValueChange={(value) => {
              setPassword(value);
              clearError("password");
            }}
            autoComplete="current-password"
            visible={passwordVisible}
            onToggleVisible={() => setPasswordVisible((v) => !v)}
          />
        </Field>

        {formErrorAlert}

        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={busy !== null}
        >
          {busy === "login" ? (
            spinner
          ) : (
            <i className="bi bi-box-arrow-in-right me-2" aria-hidden="true" />
          )}
          {busy === "login" ? "Přihlašuji…" : "Přihlásit se"}
        </button>

        <div className="text-center mt-2">
          {/* Tlačítko během prodlevy ukazuje odpočet */}
          <button
            type="button"
            className="btn btn-link"
            style={{ minHeight: "var(--lks-tap, 2.75rem)" }}
            onClick={handleResetPassword}
            disabled={busy !== null || resetCooldown > 0}
          >
            {busy === "reset" && spinner}
            {resetCooldown > 0
              ? `Zapomenuté heslo? (${resetCooldown} s)`
              : "Zapomenuté heslo?"}
          </button>
        </div>
      </Section>
    </form>
  );

  // Osobní údaje, telefon a souhlas sdílí registrace s krokem „Doplňte údaje“
  // (stejná pole, stejná id – zobrazený je vždy jen jeden z formulářů).
  const personalSection = (
    <Section title="Osobní údaje" icon="bi-person">
      <div className="row">
        <div className="col-sm-6">
          <Field id="regFirstName" label="Jméno" required error={errors.firstName}>
            <input
              type="text"
              className="form-control"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                clearError("firstName");
              }}
              autoComplete="given-name"
              autoCapitalize="words"
            />
          </Field>
        </div>
        <div className="col-sm-6">
          <Field id="regLastName" label="Příjmení" required error={errors.lastName}>
            <input
              type="text"
              className="form-control"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                clearError("lastName");
              }}
              autoComplete="family-name"
              autoCapitalize="words"
            />
          </Field>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-6">
          <Field
            id="regBirthYear"
            label="Rok narození"
            required
            error={errors.birthYear}
          >
            <Field.Number
              value={birthYear}
              onValueChange={(value) => {
                setBirthYear(value);
                clearError("birthYear");
              }}
              min={MIN_BIRTH_YEAR}
              max={currentYear}
              step={1}
              placeholder="např. 1982"
              autoComplete="bday-year"
              aria-describedby={LOCKED_PERSONAL_NOTE_ID}
            />
          </Field>
        </div>
        <div className="col-sm-6">
          <Field id="regGender" label="Pohlaví" required error={errors.gender}>
            <select
              className="form-select"
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                clearError("gender");
              }}
              aria-describedby={LOCKED_PERSONAL_NOTE_ID}
            >
              <option value="">Vyberte…</option>
              <option value="muž">Muž</option>
              <option value="žena">Žena</option>
            </select>
          </Field>
        </div>
      </div>
      {/* Rozhodnutí majitele 12. 10. 2026: po registraci je mění jen správce
          (pravidla – ownerEditableUserKeys); u převzatého záznamu klubu platí
          jeho hodnoty (utils/completeRegistration.lockedFromLegacy). */}
      <p
        id={LOCKED_PERSONAL_NOTE_ID}
        className="form-text mb-3"
        style={{ marginTop: "calc(-1 * var(--lks-sp-2, 0.5rem))" }}
      >
        Pohlaví a rok narození po registraci může změnit jen správce – zkontrolujte je.
      </p>

      <Field
        id="regNationality"
        label="Státní občanství"
        required
        error={errors.nationality}
        className={nationality === "jiná" ? undefined : "mb-0"}
      >
        <select
          className="form-select"
          value={nationality}
          onChange={(e) => {
            setNationality(e.target.value);
            clearError("nationality");
            clearError("customNationality");
          }}
        >
          <option value="">Vyberte…</option>
          <option value="česká">Česká republika</option>
          <option value="slovenská">Slovenská republika</option>
          <option value="jiná">Jiné</option>
        </select>
      </Field>

      {nationality === "jiná" && (
        <Field
          id="regNationalityOther"
          label="Jiné státní občanství"
          required
          error={errors.customNationality}
          className="mb-0"
        >
          <input
            type="text"
            className="form-control"
            placeholder="Zadejte občanství"
            value={customNationality}
            onChange={(e) => {
              setCustomNationality(e.target.value);
              clearError("customNationality");
            }}
          />
        </Field>
      )}
    </Section>
  );

  const renderPhoneField = (className?: string) => (
    <PhoneInput
      id="regPhone"
      value={phone}
      onChange={(value) => {
        setPhone(value);
        clearError("phone");
      }}
      required
      error={errors.phone}
      help="Pro případ zrušení závodu nebo potíží s platbou."
      className={className}
    />
  );

  /* Souhlas se zpracováním osobních údajů (ukládá se gdprConsentAt) */
  const consentBlock = (
    <div className="mb-3">
      <div className="form-check" style={{ minHeight: "var(--lks-tap, 2.75rem)" }}>
        <input
          id="regConsent"
          type="checkbox"
          className={`form-check-input${errors.consent ? " is-invalid" : ""}`}
          checked={consent}
          onChange={(e) => {
            setConsent(e.target.checked);
            clearError("consent");
          }}
          required
          aria-invalid={errors.consent ? true : undefined}
          aria-describedby={
            errors.consent ? "regConsent-help regConsent-error" : "regConsent-help"
          }
        />
        <label className="form-check-label" htmlFor="regConsent">
          Souhlasím se zpracováním svých osobních údajů pro registraci, správu
          účtu a účast na závodech v LKS Apce.
          <span className="lks-field__req" aria-hidden="true">
            *
          </span>
        </label>
      </div>
      <div id="regConsent-help" className="form-text">
        Co zpracováváme a proč:{" "}
        <button
          type="button"
          className="btn btn-link p-0"
          style={{ fontSize: "inherit", verticalAlign: "baseline" }}
          onClick={openGdpr}
        >
          Informace o ochraně údajů
        </button>
      </div>
      {errors.consent && (
        <div
          id="regConsent-error"
          className="invalid-feedback d-block"
          role="alert"
        >
          {errors.consent}
        </div>
      )}
    </div>
  );

  const registerForm = (
    <form noValidate onSubmit={handleRegister} autoComplete="on" method="post">
      {personalSection}

      <Section title="Kontakt a přihlášení" icon="bi-envelope">
        <Field
          id="regEmail"
          label="E-mail"
          required
          help="Slouží k přihlášení, pošleme na něj ověřovací odkaz."
          error={errors.email}
        >
          <input
            type="email"
            name="email"
            className="form-control"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearError("email");
            }}
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </Field>

        {renderPhoneField()}

        <Field
          id="regPassword"
          label="Heslo"
          required
          help={`Alespoň ${MIN_PASSWORD_LENGTH} znaků.`}
          error={errors.password}
          className="mb-0"
        >
          <PasswordControl
            value={password}
            onValueChange={(value) => {
              setPassword(value);
              clearError("password");
            }}
            autoComplete="new-password"
            visible={passwordVisible}
            onToggleVisible={() => setPasswordVisible((v) => !v)}
          />
        </Field>
      </Section>

      {consentBlock}

      {formErrorAlert}

      {/* Sticky lišta akcí – přímý potomek <form>, ne uvnitř Section */}
      <div className="lks-form-actions">
        <button type="submit" className="btn btn-success" disabled={busy !== null}>
          {busy === "register" ? (
            spinner
          ) : (
            <i className="bi bi-person-plus me-2" aria-hidden="true" />
          )}
          {busy === "register" ? "Registruji…" : "Registrovat se"}
        </button>
      </div>
    </form>
  );

  // ── Kroky dokončení (přihlášený účet bez profilu) ──────────

  /** Odhlásit se z kroku dokončení (vedle hlavní akce). */
  const signOutButton = (
    <button
      type="button"
      className="btn btn-outline-secondary"
      onClick={handleUnfinishedSignOut}
      disabled={busy !== null}
    >
      {busy === "logout" ? (
        spinner
      ) : (
        <i className="bi bi-box-arrow-right me-2" aria-hidden="true" />
      )}
      Odhlásit se
    </button>
  );

  /**
   * Úvodní vysvětlení kroku (trvalá zpráva nad formulářem). Po zobrazení
   * kroku na něj přejde fokus (stepIntroRef); action = odkaz pod textem.
   */
  const renderStepIntro = (
    icon: string,
    title: string,
    text: React.ReactNode,
    action?: React.ReactNode
  ) => (
    <div ref={stepIntroRef} className="alert alert-warning mb-3" role="status" tabIndex={-1}>
      <p className="fw-semibold mb-1">
        <i className={`bi ${icon} me-2`} aria-hidden="true" />
        {title}
      </p>
      <p className="mb-0">{text}</p>
      {action}
    </div>
  );

  /** „Doplňte údaje“ – formulář jako při registraci, e-mail z účtu, bez hesla. */
  const renderDetails = (account: User) => (
    <form noValidate onSubmit={handleDetails} autoComplete="on" method="post">
      {renderStepIntro(
        "bi-person-exclamation",
        "Doplňte prosím své údaje",
        `Registrace účtu ${account.email || ""} se nedokončila – chybí údaje člena. Vyplňte je, registrace se tím dokončí.`,
        <p className="mt-1 mb-0">
          Mazali jste účet, nebo ho už nechcete?{" "}
          <button
            type="button"
            className="btn btn-link px-0"
            style={{ minHeight: "var(--lks-tap, 2.75rem)" }}
            onClick={() => switchUnfinished("deletion")}
            disabled={busy !== null}
          >
            Chci účet smazat
          </button>
        </p>
      )}

      {personalSection}

      <Section title="Kontakt" icon="bi-envelope">
        <Field
          id="detailsEmail"
          label="E-mail"
          help="E-mail účtu, kterým se přihlašujete – změnit ho tady nejde."
        >
          <input
            type="email"
            className="form-control"
            value={account.email || ""}
            readOnly
          />
        </Field>

        {renderPhoneField("mb-0")}
      </Section>

      {consentBlock}

      {formErrorAlert}

      <div className="lks-form-actions">
        {signOutButton}
        <button type="submit" className="btn btn-success" disabled={busy !== null}>
          {busy === "details" ? (
            spinner
          ) : (
            <i className="bi bi-person-check me-2" aria-hidden="true" />
          )}
          {busy === "details" ? "Dokončuji…" : "Dokončit registraci"}
        </button>
      </div>
    </form>
  );

  /**
   * „Jste to vy?“ – import se stejným e-mailem, ale jiným jménem nebo rokem
   * narození. Jeden kandidát: ano / ne; víc kandidátů: výběr + „nejsem nikdo
   * z nich“. Vybraný import, který mezitím zmizel (gone): zmínka nad
   * dotazem, a když jiný nezbyl, otázka „Pokračovat jako nový člen?“.
   * Text důsledků je k tlačítkům / skupině připojený přes aria-describedby.
   */
  const renderConfirm = (step: Extract<Unfinished, { kind: "confirm" }>) => {
    const { candidates, gone } = step;
    const goneNote = gone ? `${goneRecordText(gone)} ` : "";
    // Rok narození a pohlaví platí ze záznamu klubu (rozhodnutí majitele
    // 12. 10. 2026, utils/completeRegistration.lockedFromLegacy) – formulář
    // je doplní, jen když v záznamu chybí.
    const consequences =
      "Když ano, převezmete jeho členské číslo, členství, rok narození a pohlaví " +
      "(chybí-li v záznamu nebo jsou neplatné, doplní se z registrace); jméno, " +
      "telefon a státní občanství se nastaví podle vaší registrace. Pohlaví a rok " +
      "narození může změnit jen správce. Když ne, dostanete nové členské číslo " +
      "a evidovaný záznam zůstane, jak je.";
    if (candidates.length === 0) {
      // Jediný vybraný import zmizel (nález 3 3. kola) – dřív aplikace bez
      // ptaní založila nového člena s novým číslem a bez členství.
      return (
        <>
          {renderStepIntro(
            "bi-person-exclamation",
            "Záznam už v evidenci není",
            `${goneNote}Pokračovat jako nový člen?`
          )}
          <Section title="Nový člen" icon="bi-person-plus">
            <p id="confirmConsequences" className="mb-3">
              Jako nový člen dostanete nové členské číslo – číslo ani členství
              z původního záznamu se nepřevedou. Pokud je chcete zachovat,
              odhlaste se a napište správci klubu na{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
            {formErrorAlert}
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => answerCandidates({ kind: "new" })}
                disabled={busy !== null}
                aria-describedby="confirmConsequences"
              >
                {busy === "confirmNew" && spinner}
                Pokračovat jako nový člen
              </button>
            </div>
          </Section>
          <div className="text-center">{signOutButton}</div>
        </>
      );
    }
    if (candidates.length === 1) {
      const candidate = candidates[0];
      return (
        <>
          {renderStepIntro(
            "bi-person-check",
            "Člen se stejným e-mailem",
            `${goneNote}K vašemu e-mailu evidujeme člena ${candidateDescription(candidate)}. Jste to vy?`
          )}
          <Section title="Člen klubu" icon="bi-person-vcard">
            <p id="confirmConsequences" className="mb-3">
              {consequences}
            </p>
            {formErrorAlert}
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => answerCandidates({ kind: "transfer", legacyId: candidate.id })}
                disabled={busy !== null}
                aria-describedby="confirmConsequences"
              >
                {busy === "confirm" && spinner}
                Ano, jsem to já
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => answerCandidates({ kind: "new" })}
                disabled={busy !== null}
                aria-describedby="confirmConsequences"
              >
                {busy === "confirmNew" && spinner}
                Ne, jsem nový člen
              </button>
            </div>
          </Section>
          <div className="text-center">{signOutButton}</div>
        </>
      );
    }
    const options: Array<[string, string]> = [
      ...candidates.map((c): [string, string] => [c.id, candidateOption(c)]),
      [NEW_MEMBER_CHOICE, "Nejsem nikdo z nich – jsem nový člen"],
    ];
    return (
      <form noValidate onSubmit={handleChoice}>
        {renderStepIntro(
          "bi-people",
          "Členové se stejným e-mailem",
          `${goneNote}K vašemu e-mailu evidujeme víc členů. Vyberte, který z nich jste vy.`
        )}
        <Section title="Člen klubu" icon="bi-person-vcard">
          {/* help = text důsledků, Field ho připojí ke skupině (aria-describedby). */}
          <Field
            id="regChoice"
            label="Jsem"
            required
            group
            help={consequences}
            error={errors.choice}
            className="mb-0"
          >
            {options.map(([value, label], index) => (
              <div className="form-check" key={value}>
                <input
                  id={`regChoice-${index}`}
                  type="radio"
                  name="regChoice"
                  className="form-check-input"
                  value={value}
                  checked={choice === value}
                  onChange={() => {
                    setChoice(value);
                    clearError("choice");
                  }}
                />
                <label className="form-check-label" htmlFor={`regChoice-${index}`}>
                  {label}
                </label>
              </div>
            ))}
          </Field>
        </Section>

        {formErrorAlert}

        <div className="lks-form-actions">
          {signOutButton}
          <button type="submit" className="btn btn-primary" disabled={busy !== null}>
            {busy === "confirm" || busy === "confirmNew" ? (
              spinner
            ) : (
              <i className="bi bi-check2 me-2" aria-hidden="true" />
            )}
            Pokračovat
          </button>
        </div>
      </form>
    );
  };

  /**
   * „Dokončit smazání účtu“ – náhrobek po smazání, kterému selhal deleteUser;
   * requested = „Chci účet smazat“ z kroku Doplňte údaje (s návratem zpět).
   */
  const renderDeletion = (account: User, requested: boolean) => (
    <form noValidate onSubmit={handleFinishDeletion} autoComplete="on" method="post">
      {requested
        ? renderStepIntro(
            "bi-trash3",
            "Smazat účet",
            `Smaže se přihlašovací účet ${account.email || ""} i údaje, které k němu jsou uložené. Smazání potvrďte heslem. Když si to rozmyslíte, vraťte se k doplnění údajů.`
          )
        : renderStepIntro(
            "bi-trash3",
            "Smazání účtu není dokončené",
            <>
              Údaje profilu účtu {account.email || ""} jsou smazané, přihlašovací účet
              ale zůstal. Smazání dokončete zadáním hesla. Pokud jste účet smazat
              nechtěli, napište správci klubu na{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </>
          )}

      <Section title={requested ? "Smazání účtu" : "Dokončit smazání"} icon="bi-trash3">
        {/* Uživatelské jméno pro správce hesel (heslo patří k tomuto účtu). */}
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={account.email || ""}
          readOnly
          hidden
        />
        <Field
          id="finishDeletePassword"
          label="Heslo"
          required
          help="Heslo, kterým se přihlašujete."
          error={errors.deletePassword}
          className="mb-0"
        >
          <PasswordControl
            value={deletePassword}
            onValueChange={(value) => {
              setDeletePassword(value);
              clearError("deletePassword");
            }}
            autoComplete="current-password"
            visible={passwordVisible}
            onToggleVisible={() => setPasswordVisible((v) => !v)}
          />
        </Field>
      </Section>

      {formErrorAlert}

      <div className="lks-form-actions">
        {requested ? (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => switchUnfinished("details")}
            disabled={busy !== null}
          >
            <i className="bi bi-arrow-left me-2" aria-hidden="true" />
            Zpět
          </button>
        ) : (
          signOutButton
        )}
        <button type="submit" className="btn btn-danger" disabled={busy !== null}>
          {busy === "finishDelete" ? (
            spinner
          ) : (
            <i className="bi bi-trash3 me-2" aria-hidden="true" />
          )}
          {requested ? "Smazat účet" : "Dokončit smazání účtu"}
        </button>
      </div>
    </form>
  );

  const renderUnfinished = (step: Unfinished) => {
    if (step.kind === "details") return renderDetails(step.user);
    if (step.kind === "confirm") return renderConfirm(step);
    return renderDeletion(step.user, step.requested === true);
  };

  return (
    <div style={{ maxWidth: "32rem", margin: "0 auto" }}>
      <div className="text-center mb-3">
        <img src={logo} alt="Logo LKS" width={64} height={64} />
      </div>

      {showSpinner ? (
        <LoadingState />
      ) : (
        <>
          {unfinished ? (
            renderUnfinished(unfinished)
          ) : (
            <>
              {notice && renderNotice(notice)}

              <p className="text-center mb-3">
                {isLogin ? "Ještě nemáte účet?" : "Už máte účet?"}{" "}
                <button
                  type="button"
                  className="btn btn-link"
                  style={{ minHeight: "var(--lks-tap, 2.75rem)" }}
                  onClick={() => switchMode(!isLogin)}
                  disabled={busy !== null}
                >
                  {isLogin ? "Registrovat se" : "Přihlásit se"}
                </button>
              </p>

              {isLogin ? loginForm : registerForm}
            </>
          )}

          {/* Mimo formuláře – viz openGdpr */}
          <div ref={gdprRef} className="text-center mt-4">
            <GdprModal />
          </div>
        </>
      )}

      {/* Verze buildu – majitel podle ní poznává nasazený build */}
      <footer
        className="mt-4"
        style={{
          color: "var(--lks-text-2)",
          borderTopColor: "var(--lks-border)",
        }}
      >
        LKS Apka verze: {buildTime}
      </footer>
    </div>
  );
};

export default AuthPage;
